/**
 * Single source of truth for the Portfolio Impact Explorer chart data +
 * options. Used by the on-screen explorer AND the report exporter so the
 * deck figures are byte-for-byte the same chart (and the economic series —
 * USD summed across ALL sections — can't drift between the two).
 */
import type { ChartOptions } from "chart.js";
import { toSigFigs } from "../../../utils/format";
import {
  getCategoryDisplayName,
  isHiddenCategory,
} from "../../model/domain/definitions";
import type { PortfolioAggregate } from "../api/portfolio";
import {
  midpointRanges,
  type ScenariosBlock,
  type MidpointSection,
} from "../../impact/domain/scenarios";

export type ImpactMetric = "economic" | "dalys" | "pdfs";

/**
 * Fleet midpoint Floor/Central/Ceiling rows for the Range-mode band strip,
 * aligned to the on-screen tab. Economic reads the CtG Economic USD array
 * (the fleet band's headline basis); Health/Biodiversity read Total_Impact.
 */
export function buildImpactRangeRows(
  scenarios: ScenariosBlock | undefined,
  metric: ImpactMetric,
) {
  const section: MidpointSection =
    metric === "economic"
      ? "Economic"
      : metric === "dalys"
        ? "Public_Health"
        : "Biodiversity";
  const rows = midpointRanges(scenarios, section)
    .filter((r) => !isHiddenCategory(r.category))
    .map((r) => ({ ...r, label: getCategoryDisplayName(r.category) }));
  const color =
    metric === "economic"
      ? "#22C55E"
      : metric === "dalys"
        ? "#FBBF24"
        : "#3B82F6";
  const max = Math.max(0, ...rows.map((r) => Math.abs(r.ceiling)));
  const format =
    metric === "economic"
      ? (n: number) => `$${toSigFigs(n / 1_000_000)}M`
      : (n: number) => toSigFigs(n);
  return { rows, color, max, format };
}

type Impacts = PortfolioAggregate["impacts"];

export function buildImpactChartData(impacts: Impacts, metric: ImpactMetric) {
  // Combined economic data from all sections (USD across Public_Health,
  // Biodiversity, Economic) — NOT just the Economic array.
  const combinedEconomicData: { Impact_Category: string; USD: number }[] = [];
  for (const section of ["Public_Health", "Biodiversity", "Economic"] as const) {
    for (const entry of impacts[section] || []) {
      if (entry.USD) {
        combinedEconomicData.push({
          Impact_Category: entry.Impact_Category,
          USD: entry.USD,
        });
      }
    }
  }
  const economicByCategory: Record<string, number> = {};
  let totalEconomicAbs = 0;
  for (const item of combinedEconomicData) {
    economicByCategory[item.Impact_Category] =
      (economicByCategory[item.Impact_Category] || 0) + item.USD;
    totalEconomicAbs += Math.abs(item.USD);
  }
  const significantEconomicData = Object.entries(economicByCategory)
    .map(([cat, value]) => ({ Impact_Category: cat, USD: value }))
    .filter(
      (d) =>
        totalEconomicAbs > 0 &&
        (Math.abs(d.USD) / totalEconomicAbs) * 100 > 1.0,
    )
    .sort((a, b) => Math.abs(b.USD) - Math.abs(a.USD));

  const publicHealthData = [...(impacts.Public_Health || [])].sort(
    (a, b) => Math.abs(b.Total_Impact || 0) - Math.abs(a.Total_Impact || 0),
  );
  const biodiversityData = [...(impacts.Biodiversity || [])].sort(
    (a, b) => Math.abs(b.Total_Impact || 0) - Math.abs(a.Total_Impact || 0),
  );

  let labels: string[];
  let values: number[];
  let dataField: string;
  if (metric === "economic") {
    const visible = significantEconomicData.filter(
      (d) => !isHiddenCategory(d.Impact_Category),
    );
    labels = visible.map((d) => getCategoryDisplayName(d.Impact_Category));
    values = visible.map((d) => d.USD / 1_000_000);
    dataField = "Economic Benefit (Million $)";
  } else if (metric === "dalys") {
    const visible = publicHealthData.filter(
      (d) => !isHiddenCategory(d.Impact_Category),
    );
    labels = visible.map((d) => getCategoryDisplayName(d.Impact_Category));
    values = visible.map((d) => d.Total_Impact || 0);
    dataField = "Disability Adjusted Life Years (DALY)";
  } else {
    const visible = biodiversityData.filter(
      (d) => !isHiddenCategory(d.Impact_Category),
    );
    labels = visible.map((d) => getCategoryDisplayName(d.Impact_Category));
    values = visible.map((d) => d.Total_Impact || 0);
    dataField = "Potentially Disappeared Fraction (PDF)";
  }

  const positiveColor =
    metric === "economic"
      ? "#22C55E"
      : metric === "dalys"
        ? "#FBBF24"
        : "#3B82F6";
  const negativeColor = "#EF4444";

  return {
    hasData: labels.length > 0,
    data: {
      labels,
      datasets: [
        {
          label: dataField,
          data: values,
          backgroundColor: values.map((v) =>
            v >= 0 ? positiveColor : negativeColor,
          ),
          barThickness: "flex" as const,
          barPercentage: 0.8,
        },
      ],
    },
  };
}

export function buildImpactChartOptions(
  metric: ImpactMetric,
): ChartOptions<"bar"> {
  return {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1E293B",
        titleColor: "#F8FAFC",
        bodyColor: "#CBD5E1",
        borderColor: "#334155",
        borderWidth: 1,
        callbacks: {
          label(context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.x !== null) {
              label +=
                metric === "economic"
                  ? `$${toSigFigs(context.parsed.x)}M`
                  : toSigFigs(context.parsed.x);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: "#334155" },
        ticks: {
          color: "#94A3B8",
          callback(tickValue) {
            const v = Number(tickValue);
            return metric === "economic"
              ? `$${toSigFigs(v)}M`
              : toSigFigs(v);
          },
        },
      },
      y: { grid: { display: false }, ticks: { color: "#CBD5E1" } },
    },
  };
}

// ---------------------------------------------------------------------------
// Generation chart (matches PortfolioGenerationChart on the dashboard)
// ---------------------------------------------------------------------------

const FUEL_COLORS: Record<string, string> = {
  SUN: "#FBBF24",
  WND: "#14B8A6",
  COL: "#6B7280",
  NG: "#F97316",
  NUC: "#A855F7",
  WAT: "#3B82F6",
  OIL: "#92400E",
  OTH: "#D1D5DB",
};

function getFuelColor(fuelType: string): string {
  if (FUEL_COLORS[fuelType]) return FUEL_COLORS[fuelType];
  const key = Object.keys(FUEL_COLORS).find((k) =>
    fuelType.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? FUEL_COLORS[key] : "#9CA3AF";
}

export function buildGenerationChartData(
  generation: Record<string, { Difference: number }>,
) {
  const rows = Object.entries(generation)
    .map(([fuel, v]) => ({
      raw: fuel,
      label: fuel.replace(/_/g, " "),
      value: Number(v?.Difference) || 0,
    }))
    .filter((r) => Math.abs(r.value) > 0.001)
    .sort((a, b) => b.value - a.value);
  return {
    hasData: rows.length > 0,
    data: {
      labels: rows.map((r) => r.label),
      datasets: [
        {
          label: "Net change (GWh)",
          data: rows.map((r) => r.value),
          backgroundColor: rows.map((r) => getFuelColor(r.raw)),
          barThickness: "flex" as const,
          barPercentage: 0.8,
        },
      ],
    },
  };
}

export function buildGenerationChartOptions(): ChartOptions<"bar"> {
  return {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { color: "#334155" },
        ticks: {
          color: "#94A3B8",
          callback(tickValue) {
            const v = Number(tickValue);
            const sign = v > 0 ? "+" : "";
            return Math.abs(v) >= 1
              ? `${sign}${Math.round(v)}`
              : `${sign}${(v * 1000).toFixed(0)} MWh`;
          },
        },
      },
      y: { grid: { display: false }, ticks: { color: "#CBD5E1" } },
    },
  };
}

export const IMPACT_DESCRIPTIONS: Record<ImpactMetric, string> = {
  economic:
    "Monetized value of avoided health and environmental damage across portfolio",
  dalys: "Health benefits from reduced air pollution and environmental damage",
  pdfs: "Biodiversity preserved through reduced ecosystem damage",
};
